/**
 * Shown when user cancels Stripe Checkout or payment fails (Phase 3/4).
 * Reads token from URL, calls backend to record attempt; shows Retry or redirects home with banner.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Button, Container, Typography, CircularProgress, Alert } from '@mui/material';
import NavBar from '../components/common/NavBar';
import api from '../services/api';
import { BILLING_RECORD_CHECKOUT_CANCELED, BILLING_CHECKOUT_RETRY } from '../config/Config';

export default function PaymentFailedPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(!!token);
  const [retryToken, setRetryToken] = useState(null);
  const [redirectHome, setRedirectHome] = useState(false);
  const [banner, setBanner] = useState(null);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid link. Go to login and complete payment from your account.');
      setLoading(false);
      return;
    }
    api
      .post(BILLING_RECORD_CHECKOUT_CANCELED, { token })
      .then(({ data }) => {
        setRedirectHome(!!data.redirect_home);
        setBanner(data.banner || null);
        if (data.retry_token) setRetryToken(data.retry_token);
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.response?.data?.banner || err.message || 'Something went wrong.';
        setError(msg);
        if (err.response?.data?.redirect_home) setRedirectHome(true);
        if (err.response?.data?.banner) setBanner(err.response.data.banner);
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (redirectHome && !loading) {
      navigate('/', { replace: true, state: { banner } });
    }
  }, [redirectHome, loading, banner, navigate]);

  const handleRetry = () => {
    if (!retryToken) return;
    setRetrying(true);
    api
      .post(BILLING_CHECKOUT_RETRY, { retry_token: retryToken })
      .then(({ data }) => {
        if (data.url) window.location.href = data.url;
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Failed to start payment. Try again.');
      })
      .finally(() => setRetrying(false));
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
        <NavBar />
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
      <NavBar />
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Payment was not completed. You can retry below or log in later to complete payment from your account.
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {retryToken && (
          <Button variant="contained" size="large" onClick={handleRetry} disabled={retrying} fullWidth>
            {retrying ? <CircularProgress size={24} /> : 'Retry Payment'}
          </Button>
        )}
        <Button variant="text" onClick={() => navigate('/login')} sx={{ mt: 2 }} fullWidth>
          Go to Login
        </Button>
      </Container>
    </Box>
  );
}
